import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, type SyntheticEvent } from "react";

import {
  WorkspaceAppearanceUpdateSchema,
  WorkspaceSettingsUpdateSchema,
  WorkspaceSubdomainUpdateSchema,
} from "../../../shared/schemas";
import {
  ApiClientError,
  navigateToWorkspace,
  removeWorkspaceFavicon,
  removeWorkspaceLogo,
  updateWorkspace,
  updateWorkspaceAppearance,
  updateWorkspaceSubdomain,
  uploadWorkspaceFavicon,
  uploadWorkspaceLogo,
} from "../../api/client";
import { appsQuery, meQuery, workspaceQuery, workspaceSubdomainQuery } from "../../api/queries";

function useWorkspaceResources(organizationId: string, isOnline: boolean, enabled: boolean) {
  const queryClient = useQueryClient();
  const workspace = useQuery({
    ...workspaceQuery(organizationId),
    enabled: isOnline && Boolean(organizationId) && enabled,
  });
  const address = useQuery({
    ...workspaceSubdomainQuery(organizationId),
    enabled: isOnline && Boolean(organizationId) && enabled,
  });
  async function refresh() {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: workspaceQuery(organizationId).queryKey }),
      queryClient.invalidateQueries({ queryKey: workspaceSubdomainQuery(organizationId).queryKey }),
      queryClient.invalidateQueries({ queryKey: appsQuery(organizationId).queryKey }),
      queryClient.invalidateQueries({ queryKey: meQuery.queryKey }),
    ]);
  }
  return { address, refresh, workspace };
}

function useWorkspaceEdits(organizationId: string) {
  const [nameEdit, setNameEdit] = useState<{ organizationId: string; value: string } | null>(null);
  const [subdomainEdit, setSubdomainEdit] = useState<{
    organizationId: string;
    value: string;
  } | null>(null);
  const [colorEdit, setColorEdit] = useState<{ organizationId: string; value: string } | null>(
    null,
  );
  const [nameError, setNameError] = useState("");
  const [subdomainError, setSubdomainError] = useState("");
  const [colorError, setColorError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const scopedValue = (edit: typeof nameEdit, fallback: string) =>
    edit?.organizationId === organizationId ? edit.value : fallback;
  return {
    colorEdit,
    colorError,
    nameEdit,
    nameError,
    scopedValue,
    setColorEdit,
    setColorError,
    setNameEdit,
    setNameError,
    setSubdomainEdit,
    setSubdomainError,
    setSuccessMessage,
    subdomainEdit,
    subdomainError,
    successMessage,
  };
}

function useWorkspaceMutations(
  organizationId: string,
  refresh: () => Promise<void>,
  edits: ReturnType<typeof useWorkspaceEdits>,
) {
  const name = useMutation({
    mutationFn: (value: string) => updateWorkspace(organizationId, { name: value }),
    onSuccess: async () => {
      await refresh();
      edits.setNameEdit(null);
    },
  });
  const color = useMutation({
    mutationFn: (primaryColor: string | null) =>
      updateWorkspaceAppearance(organizationId, { primaryColor }),
    onSuccess: async () => {
      await refresh();
      edits.setColorEdit(null);
    },
  });
  const subdomain = useMutation({
    mutationFn: (value: string) => updateWorkspaceSubdomain(organizationId, { subdomain: value }),
    onSuccess: () => {
      navigateToWorkspace(
        organizationId,
        `/workspace?org=${encodeURIComponent(organizationId)}&tab=settings`,
      );
    },
  });
  const logo = useMutation({
    mutationFn: (file: File) => uploadWorkspaceLogo(organizationId, file),
    onSuccess: refresh,
  });
  const removeLogo = useMutation({
    mutationFn: () => removeWorkspaceLogo(organizationId),
    onSuccess: refresh,
  });
  const favicon = useMutation({
    mutationFn: (file: File) => uploadWorkspaceFavicon(organizationId, file),
    onSuccess: refresh,
  });
  const removeFavicon = useMutation({
    mutationFn: () => removeWorkspaceFavicon(organizationId),
    onSuccess: refresh,
  });
  return { color, favicon, logo, name, removeFavicon, removeLogo, subdomain };
}

function firstIssue(error: { issues: { message: string }[] }, fallback: string): string {
  return error.issues[0]?.message ?? fallback;
}

export function useWorkspaceController(
  organizationId: string,
  isOnline: boolean,
  canManageWorkspace: boolean,
) {
  const resources = useWorkspaceResources(organizationId, isOnline, canManageWorkspace);
  const edits = useWorkspaceEdits(organizationId);
  const mutations = useWorkspaceMutations(organizationId, resources.refresh, edits);
  const workspaceName = edits.scopedValue(
    edits.nameEdit,
    resources.workspace.data?.organization.name ?? "",
  );
  const workspaceSubdomain = edits.scopedValue(
    edits.subdomainEdit,
    resources.address.data?.subdomain ?? "",
  );
  const primaryColor = edits.scopedValue(
    edits.colorEdit,
    resources.workspace.data?.organization.primaryColor ?? "#1d5583",
  );
  const pickerColor =
    WorkspaceAppearanceUpdateSchema.shape.primaryColor.unwrap().safeParse(primaryColor).data ??
    "#1d5583";

  function saveName(event: SyntheticEvent<HTMLFormElement>) {
    event.preventDefault();
    const parsed = WorkspaceSettingsUpdateSchema.safeParse({ name: workspaceName });
    if (!parsed.success) {
      edits.setNameError(firstIssue(parsed.error, "Enter a workspace name."));
      return;
    }
    edits.setNameError("");
    mutations.name.mutate(parsed.data.name);
  }
  function saveSubdomain(event: SyntheticEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!resources.address.data?.canEdit) return;
    const parsed = WorkspaceSubdomainUpdateSchema.safeParse({ subdomain: workspaceSubdomain });
    if (!parsed.success) {
      edits.setSubdomainError(firstIssue(parsed.error, "Enter a workspace subdomain."));
      return;
    }
    edits.setSubdomainError("");
    mutations.subdomain.mutate(parsed.data.subdomain);
  }
  function saveColor(event: SyntheticEvent<HTMLFormElement>) {
    event.preventDefault();
    const parsed = WorkspaceAppearanceUpdateSchema.safeParse({ primaryColor });
    if (!parsed.success) {
      edits.setColorError(firstIssue(parsed.error, "Enter a primary color."));
      return;
    }
    edits.setColorError("");
    mutations.color.mutate(parsed.data.primaryColor);
  }
  const error = Object.values(mutations)
    .map((mutation) => mutation.error)
    .find(Boolean);
  const mutationMessage =
    error instanceof ApiClientError
      ? error.message
      : error
        ? "The workspace could not be updated. Try again."
        : null;
  return {
    edits,
    mutations,
    mutationMessage,
    pickerColor,
    primaryColor,
    resources,
    saveColor,
    saveName,
    saveSubdomain,
    workspaceName,
    workspaceSubdomain,
  };
}
